/**
 * Route-level workspace access predicates — deliberately dependency-free.
 *
 * `App.js` needs these to decide whether an HR route is reachable, and App.js is in the EAGER entry
 * chunk that the landing page ships. Importing them from `lib/performance` / `lib/learningDevelopment`
 * pulled those whole modules (~88 KB raw) into `main.*.js` for two permission checks, even though every
 * real consumer of those libs is behind a lazy route. Keep anything that App.js imports free of heavy
 * module graphs, or the bundle-budget gate (`scripts/check_bundle_budget.mjs`, 300 KB gz) pays for it.
 *
 * The gate builders stay in their domain modules — those are the full vocabularies. Only the two
 * route-admission predicates live here, and the domain modules re-export them so existing callers and
 * their tests are untouched.
 */

const allows = (can, permission) => typeof can === "function" && Boolean(can(permission));

/** Performance workspace: readable AND at least one operating grant beyond plain read. */
export function hasPerformanceWorkspaceAccess(can) {
  return (
    allows(can, "performance:read") &&
    (
      allows(can, "performance:configure") ||
      allows(can, "performance:update") ||
      allows(can, "performance:approve") ||
      allows(can, "performance:read_all") ||
      allows(can, "performance_sensitive:read")
    )
  );
}

/** Learning & development workspace: readable AND at least one governing grant. */
export function hasLearningDevelopmentWorkspaceAccess(can) {
  return (
    allows(can, "learning:read") &&
    (
      allows(can, "learning:configure") ||
      allows(can, "learning:approve") ||
      allows(can, "learning:read_all")
    )
  );
}
